/**
 * Dashboard — IA screen 3, owned by F29/M29 (the shell owns it; the numbers
 * belong to the features they come from).
 *
 * "Health-at-a-glance" per 08 §4. Every tile is a **link to the list behind it**,
 * because 08 §7's content rule is that every count links to its records — a
 * dead-end number invites a screenshot and a question rather than an action.
 */
import {
  Button,
  Card,
  DefinitionList,
  EmptyState,
  HelpText,
  Skeleton,
  StatGrid,
  StatTile,
} from '@dedupr/ui';
import './Dashboard.css';
import { api, ErrorState, useQuery } from '@dedupr/app-kit';
import { ApiError } from '@dedupr/api-client';
import { revealSection } from '../revealSection.js';
import { openLocalStore } from '../local/store.js';
import { useState } from 'react';
import { deriveWork, isAllClear, type WorkItem, type WorkState } from './dashboardWork.js';
import { Connect } from './Connect.js';
import { SyncPanel } from './SyncPanel.js';
import { Matches } from './Matches.js';
import { HostedMatches } from './HostedMatches.js';
import { Actions } from './Actions.js';
import { HostedActions } from './HostedActions.js';
import { Findings } from './Findings.js';
import { HostedFindings } from './HostedFindings.js';
import { accessToken } from '../auth.js';

/**
 * Whether anything is connected, for the work queue.
 *
 * ⚠️ **Returns `null` on failure, never `[]`.** "We could not ask" and "you have connected nothing"
 * are opposite answers, and the second one puts a *Connect a system* prompt in front of somebody who
 * already has one — the same defect the ops audit hit with `connections` in 2026-08-10.
 */
async function loadConnections(): Promise<{ provider: string; needsReconnect: boolean }[] | null> {
  try {
    const token = await accessToken();
    const res = await fetch('/api/relay/connections', {
      headers: token ? { authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      connections?: { provider: string; needsReconnect?: boolean }[];
    };
    if (!Array.isArray(body.connections)) return null;
    return body.connections.map((c) => ({
      provider: c.provider,
      needsReconnect: c.needsReconnect === true,
    }));
  } catch {
    return null;
  }
}

interface Tile {
  label: string;
  /** Null when the figure could not be loaded — never silently rendered as 0. */
  count: number | null;
  detail: string;
  route: string;
  tone: 'default' | 'attention';
}

export function Dashboard({
  orgId,
  localMode,
  onNavigate,
}: {
  orgId: string | undefined;
  /**
   * ⚠️ **The tier, as its own fact.** Until 2026-08-15 this screen received
   * `orgId={localMode ? undefined : orgId}` and inferred the tier from the org id being missing —
   * which did not merely select a branch, it removed the identity every connection lookup needs. An
   * Entry/Sandbox client with a live HubSpot connection was shown the first-run "Start with a file"
   * card and no connector at all. `Sources` had already been fixed the correct way.
   */
  localMode: boolean;
  onNavigate: (route: string) => void;
}) {
  const data = useQuery(async () => {
    if (!orgId) return null;
    // Settled, not `all`: one failing endpoint must degrade its own tile rather
    // than blank the dashboard. A dashboard that disappears because a single
    // count failed is worse than one honest gap.
    const [sources, findings, pairs, actions] = await Promise.allSettled([
      api.listSources({ org_id: orgId }),
      api.listFindings({ org_id: orgId, state: 'open' }),
      api.listCandidatePairs({ org_id: orgId, tier: 'review', undecided: true }),
      api.listActions({ org_id: orgId, state: 'proposed' }),
    ]);
    const val = <T,>(r: PromiseSettledResult<T>) => (r.status === 'fulfilled' ? r.value : null);
    /* ⚠️ **`not_provisioned` is a KNOWN STATE, not a failure, and collapsing the two was rule 5 in
       a new place.** `val()` turns every rejection into `null`, so a client whose data plane has
       simply never been stamped out (S6) saw four tiles reading "Could not load this figure" — the
       same words a real outage produces. Found by signing in live as a client with an org and no
       `deployment_record`: the router sends them here deliberately (`stay` is F26's setup-journey
       state) and this screen then told them something was broken.
       Only when EVERY figure says so: one endpoint answering `not_provisioned` while others return
       data would be a genuine inconsistency, and must keep reading as the error it is. */
    const reason = (r: PromiseSettledResult<unknown>) =>
      r.status === 'rejected' && r.reason instanceof ApiError ? r.reason.code : null;
    const settled = [sources, findings, pairs, actions];
    const notProvisioned = settled.every((r) => reason(r) === 'not_provisioned');
    return {
      sources: val(sources),
      findings: val(findings),
      pairs: val(pairs),
      actions: val(actions),
      notProvisioned,
    };
  }, [orgId]);

  /* ⚠️ **Z-1.** This branch used to render "No organisation yet" and nothing else, so a local-mode
     user's FIRST screen — the app's landing route — was a single sentence about a state they are not
     in and cannot reach. The whole product looked like it did not exist.
     Local mode has no org by design (F15: the engine runs on the device, there is nothing to belong
     to), so "no org" is not an error condition here — it is the normal case, and the numbers come
     from IndexedDB instead of `/v1/`. */
  /* ⚠️ **Branch on the TIER, not on missing identity.** `orgId` is still passed down, because an
     Entry/Sandbox client has one and their connections cannot be read without it — which is exactly
     what the previous `!orgId` test destroyed. */
  if (localMode) return <LocalDashboard orgId={orgId} onNavigate={onNavigate} />;
  /* A hosted client with no org resolved yet is mid-onboarding, not local. Nothing to read. */
  if (!orgId) return <EmptyState title="Setting up your organisation" description="Your account exists but is not attached to an organisation yet. This screen fills in once it is." />;

  if (data.status === 'loading') {
    /* The skeleton uses the SAME grid as the loaded state. It used a local grid with a different
       `minmax`, so the placeholder reflowed at a different width than the tiles replacing it and
       the row jumped on load — visible only in a browser, and only for the second the data takes
       to arrive. */
    return (
      <div aria-busy="true">
        <StatGrid min={240}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rect" height="6rem" />
          ))}
        </StatGrid>
      </div>
    );
  }

  if (data.status === 'error') {
    return <ErrorState code={data.errorCode} message={data.errorMessage} onRetry={data.reload} />;
  }

  // Belt as well as braces: useQuery now reports a deps change as `loading`, but
  // this fetcher can also legitimately resolve to null (no org in scope), and a
  // screen should not dereference a nullable success either way.
  const d = data.data;
  if (!d) {
    /* The skeleton uses the SAME grid as the loaded state. It used a local grid with a different
       `minmax`, so the placeholder reflowed at a different width than the tiles replacing it and
       the row jumped on load — visible only in a browser, and only for the second the data takes
       to arrive. */
    return (
      <div aria-busy="true">
        <StatGrid min={240}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rect" height="6rem" />
          ))}
        </StatGrid>
      </div>
    );
  }

  /* Said plainly, and never as a number. A count of zero here would be a claim about their data;
     the truth is that there is nowhere to count yet. */
  if (d.notProvisioned) {
    return (
      <EmptyState
        title="Your workspace is still being set up"
        description="Your organisation exists, but the infrastructure that holds your records has not been created yet — so there is nothing to show rather than nothing to find. Nobody needs to do anything; this screen fills in once it is ready."
      />
    );
  }

  const unhealthy =
    d.sources?.data.filter((s) => s.state === 'auth_error' || s.state === 'degraded').length ?? null;

  /* ⚠️ **Degraded ONLY, for the queue.** `unhealthy` above counts `auth_error` *and* `degraded`, and
     it must keep doing so — it is the tile's figure, where "needs attention" is one idea. But the
     queue already raises every `auth_error` source as its own reconnect item, so passing the same
     figure here listed one broken source twice: "sharepoint needs reconnecting" directly above "1
     source not syncing cleanly", which is the same source wearing a different sentence. Two entries
     for one problem makes the queue a liar about how much is wrong. Found in a browser; no test
     asked whether two items described the same thing. */
  const degradedOnly = d.sources?.data.filter((s) => s.state === 'degraded').length ?? null;

  const tiles: Tile[] = [
    {
      label: 'Connected sources',
      count: d.sources?.data.length ?? null,
      detail:
        unhealthy && unhealthy > 0
          ? `${unhealthy} need attention — findings from them may be stale`
          : 'All syncing normally',
      route: '/sources',
      tone: unhealthy && unhealthy > 0 ? 'attention' : 'default',
    },
    {
      label: 'Open findings',
      count: d.findings?.data.length ?? null,
      detail: 'Ranked next steps for your data',
      route: '/findings',
      tone: 'default',
    },
    {
      label: 'Pairs to review',
      count: d.pairs?.data.length ?? null,
      detail: 'Undecided duplicates in the review tier',
      route: '/matches',
      tone: 'default',
    },
    {
      label: 'Actions awaiting you',
      count: d.actions?.data.length ?? null,
      detail: 'Proposed changes, none applied without approval',
      route: '/actions',
      tone: d.actions && d.actions.data.length > 0 ? 'attention' : 'default',
    },
  ];

  const anyMissing = tiles.some((t) => t.count === null);

  /* The queue comes FIRST, above the tiles. The tiles are context — what is true — and the queue is
     the work. A screen that leads with figures asks the reader to do the triage the product should
     have done for them. */
  const work = deriveWork({
    /* ⚠️ **This was `null`, with a comment claiming the "could not check" item was unreachable here
       "by construction". It was reachable, and the browser showed it**: a hosted client was told we
       could not check their connections, on a screen that had just listed three sources. The comment
       reasoned about intent; `deriveWork` reads the value, and `null` means unknown wherever it
       appears. Asserting a property in prose is not the same as having it.
       On this tier a source IS the connection — `/v1/sources` carries both provider and state — so
       the honest mapping is the real list, and `null` stays reserved for the case where that call
       genuinely failed. */
    connections:
      d.sources?.data.map((s) => ({ provider: s.provider, needsReconnect: s.state === 'auth_error' })) ??
      null,
    unhealthySources: degradedOnly,
    records: null,
    undecidedPairs: d.pairs?.data.length ?? null,
    proposedActions: d.actions?.data.length ?? null,
    openFindings: d.findings?.data.length ?? null,
  });

  return (
    <>
      {/* ⚠️ `connections: null` above is deliberate and NOT a stub: the hosted tier's connection
          health already arrives as `unhealthySources` from `/v1/sources`, and asking the relay for
          the same fact would be a second source of truth for one question. The queue's
          "we could not check" item is therefore unreachable here by construction rather than by
          luck — `unhealthySources` carries its own null. */}
      <WorkQueue items={work} orgId={orgId} localMode={false} onNavigate={onNavigate} />
      {/* ⚠️ **`StatGrid`/`StatTile`, not a hand-rolled tile ([B-94](../../../docs/platform/TODO.md)).**
          This screen open-coded a stat card and its stylesheet claimed the geometry was "the
          prototype's stat card" — while the catalogue entry built from the same source claimed
          the same thing at different numbers. Measured against
          `docs/design-system/prototype/pages/Dedupr Platform.dc.html` (`roiCards`), the prototype
          is `padding: 18px 20px` · `border-radius: 14px` · label `11.5px/600/.05em` uppercase ·
          value `26px/600/-.5px` tabular. `StatTile` matches it; this one was 16px radius, 20px
          padding and a **14px sentence-case label** above a 30px figure — four values off at once,
          in the direction that reads as a scoreboard rather than as four different things.

          `variant="placeholder"` carries the not-knowable case, which is why the em dash and the
          "could not load" note below survive the migration intact: a `null` count must never
          render as `0`, and the component has a state for exactly that. */}
      <StatGrid min={240}>
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={tile.count === null ? '' : new Intl.NumberFormat().format(tile.count)}
            note={tile.count === null ? 'Could not load this figure' : tile.detail}
            variant={
              tile.count === null ? 'placeholder' : tile.tone === 'attention' ? 'tinted' : 'plain'
            }
            tone={tile.tone === 'attention' ? 'warning' : undefined}
            onSelect={() => onNavigate(tile.route)}
            /* "Connected sources, 3" is a fact; "3" alone is a riddle. Spelled out rather than
               left to the browser's concatenation, which announces "3Connected sources". */
            actionLabel={`${tile.label}: ${
              tile.count === null ? 'could not be loaded' : tile.count
            }. ${tile.count === null ? '' : tile.detail}`.trim()}
          />
        ))}
      </StatGrid>

      {anyMissing ? (
        <HelpText tone="error" role="alert">
          One or more figures could not be loaded. They are shown as “—” rather than zero, because a
          zero here would read as “nothing to do”.
        </HelpText>
      ) : null}

      <Card title="What is not here yet" headingLevel={2}>
        <DefinitionList
          items={[
            {
              term: 'Savings to date',
              value: 'Not shown',
              hint: 'Needs a configured rate; a monetary figure without its formula is not shown at all (08 §7, ADR-045).',
            },
            {
              term: 'Trend over time',
              value: 'Not shown',
              hint: 'Needs more than one sync run to compare against.',
            },
          ]}
        />
      </Card>
    </>
  );
}

/**
 * The dashboard for a device with no organisation — F15's local mode.
 *
 * Same tile grid and the same rules as the hosted one: a figure that could not be read renders as
 * "—" and never as 0, and every tile links to the screen that holds the records behind it.
 *
 * ⚠️ **Every route here points at a screen that actually works today** (`/sources`, `/matches`).
 * Linking a tile to `/findings` or `/actions` would put a live number in front of a placeholder,
 * which is worse than not showing the number — the count would be real and the destination empty.
 * When Z-4 builds `/actions`, "merges applied" moves there; until then it is a line of text rather
 * than a link to nowhere.
 */
/**
 * ⚠️ **Takes `orgId`, because an Entry/Sandbox client HAS one.** Its connections live behind the
 * relay and cannot be read without it — the reason the old `!orgId` branch could only ever offer a
 * file. "Local" describes where the *engine* runs, never whether the client has an identity.
 */
function LocalDashboard({
  orgId,
  onNavigate,
}: {
  orgId: string | undefined;
  onNavigate: (route: string) => void;
}) {
  const connections = useQuery(loadConnections, []);
  const data = useQuery(async () => {
    const store = await openLocalStore();
    try {
      const [sources, records, run, golden] = await Promise.all([
        store.listSources(),
        store.allRecords(),
        store.latestRun(),
        Promise.resolve(null),
      ]);
      const [pairs, entities, decisions, applied] = run
        ? await Promise.all([
            store.pairsFor(run.id),
            store.entitiesFor(run.id),
            store.decisions(),
            store.goldenFor(run.id),
          ])
        : [[], [], [], []];
      void golden;
      return { sources, records: records.length, pairs, entities, decisions, applied };
    } finally {
      /* Closed even on the error path: an open connection blocks a version upgrade indefinitely,
         which is how a DB_VERSION bump hangs for a user who left a tab open. */
      store.close();
    }
  }, []);

  if (data.status === 'loading') {
    /* The skeleton uses the SAME grid as the loaded state. It used a local grid with a different
       `minmax`, so the placeholder reflowed at a different width than the tiles replacing it and
       the row jumped on load — visible only in a browser, and only for the second the data takes
       to arrive. */
    return (
      <div aria-busy="true">
        <StatGrid min={240}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rect" height="6rem" />
          ))}
        </StatGrid>
      </div>
    );
  }

  if (data.status === 'error') {
    /* IndexedDB is genuinely refused in Safari private browsing and Firefox's "never remember
       history", so this is a real state rather than a defensive branch. */
    return <ErrorState code={data.errorCode} message={data.errorMessage} onRetry={data.reload} />;
  }

  const d = data.data;

  /* Nothing loaded yet. ⚠️ This is the state a first-time visitor lands in, and the one thing it
     must do is say where to start — the previous version of this screen did not, which is how the
     product read as "one upload box and nothing else". */
  /* ⚠️ **This branch used to be the whole screen for a connected client, and that was the defect.**
     `d.sources` is FILES on this device, so someone with a live HubSpot connection and no CSV fell
     in here and saw a single upload box — the product reading as one feature. It now only means
     "no local file", which is one input among several, so the queue decides what to show. */
  const localWork = deriveWork({
    /* `?? null` rather than a cast: `useQuery` can resolve successfully to `undefined`, and the one
       thing this field must never be is accidentally-absent. Unknown collapses to `null`, which the
       queue reports as "we could not check" rather than "you have none". */
    connections: connections.status === 'success' ? (connections.data ?? null) : null,
    unhealthySources: null,
    records: d ? d.records : 0,
    undecidedPairs: d ? d.pairs.filter((p) => !d.decisions.some((x) => x.pair_id === p.id)).length : 0,
    proposedActions: null,
    openFindings: null,
  });

  if (!d || (d.sources.length === 0 && localWork.length > 0)) {
    return (
      <>
        <WorkQueue items={localWork} orgId={orgId} localMode onNavigate={onNavigate} />
        <Card title="Or start with a file">
          <p className="web-dash__intro">
            Load a CSV or Excel export and Dedupr will map the columns, profile every field and find
            the duplicates — all in this browser. Nothing is uploaded.
          </p>
        {/* The catalogue Button, not a bespoke CTA. A hand-rolled one needed accent tokens that do
            not exist (`--accent-solid-bg` and friends) — lint:tokens caught the invention, and the
            answer was the primitive that already solves this rather than three new tokens. */}
        {/* ⚠️ `/sources`, not the deleted `/local`. This was the first-run call to action on the
            local Dashboard and it landed straight back on the Dashboard — the emptiest possible
            version of the trap, because the screen it returned to was the one you were already
            looking at, so it read as a dead button rather than a wrong destination. */}
          <Button
            onClick={() => {
              onNavigate('/sources');
              revealSection('sources-intake');
            }}
          >
            Choose a file
          </Button>
        </Card>
      </>
    );
  }

  const clusters = d.entities.filter((e) => e.members.length > 1);
  const undecided = d.pairs.filter((pair) => !d.decisions.some((dec) => dec.pair_id === pair.id));
  const liveMerges = d.applied.filter((g) => !g.rolled_back_at);

  const tiles: Tile[] = [
    {
      label: 'Files loaded',
      count: d.sources.length,
      detail: `${new Intl.NumberFormat().format(d.records)} records read on this device`,
      route: '/sources',
      tone: 'default',
    },
    {
      label: 'Duplicate groups',
      count: clusters.length,
      detail:
        clusters.length > 0
          ? `${new Intl.NumberFormat().format(clusters.reduce((n, e) => n + e.members.length, 0))} records involved`
          : 'Nothing in these files looked like a duplicate',
      route: '/matches',
      tone: 'default',
    },
    {
      label: 'Still to decide',
      count: undecided.length,
      detail: undecided.length > 0 ? 'Each one waits for you — nothing decides itself' : 'Everything found has been decided',
      route: '/matches',
      tone: undecided.length > 0 ? 'attention' : 'default',
    },
    {
      label: 'Merges applied',
      count: liveMerges.length,
      detail:
        liveMerges.length > 0
          ? 'Reflected in the file you download; undo any of them'
          : 'None yet — a merge changes only your download',
      route: '/matches',
      tone: 'default',
    },
  ];

  return (
    <>
      {/* Queue first here too — the two tiers must not diverge on the shape of the screen, only on
          where the numbers come from. */}
      <WorkQueue items={localWork} orgId={orgId} localMode onNavigate={onNavigate} />

      {/* Same catalogue entry as the hosted tiles above — the two modes must not diverge on
          chrome, which was the whole point of the 2026-08-06 reshape. */}
      <StatGrid min={240}>
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={new Intl.NumberFormat().format(tile.count ?? 0)}
            note={tile.detail}
            variant={tile.tone === 'attention' ? 'tinted' : 'plain'}
            tone={tile.tone === 'attention' ? 'warning' : undefined}
            onSelect={() => onNavigate(tile.route)}
            actionLabel={`${tile.label}: ${tile.count}. ${tile.detail}`}
          />
        ))}
      </StatGrid>

      <Card title="This is local mode" headingLevel={2}>
        <DefinitionList
          items={[
            {
              term: 'Where your data is',
              value: 'On this device only',
              hint: 'The engine runs in your browser. No records are uploaded, and closing the tab does not lose them — they are kept in this browser\'s storage.',
            },
            {
              term: 'Connected systems',
              /* ⚠️ **This was the literal string "Not connected yet", and it was FALSE on screen.**
                 The same org showed `HubSpot — CONNECTED (1)` on Sources while this card told them
                 they had connected nothing. A hardcoded claim about live state is the "readers with
                 no writer" defect inverted: prose that no data backs. `null` (could not read) is
                 kept distinct from an empty list, because "we could not ask" must never render as
                 "you have none". */
              value:
                connections.status !== 'success' || !connections.data
                  ? 'Could not be read'
                  : connections.data.length === 0
                    ? 'Not connected yet'
                    : connections.data.map((c) => c.provider).join(', '),
              /* ⚠️ Was "Not in local mode — pulling needs an account". Both halves became wrong the
                 moment an Entry/Sandbox client could reach this screen: they HAVE an account, and
                 pulling is now built (ADR-074) and reachable on Sources. What is actually missing is
                 an OAuth app for the provider (B-130), which is a deployment fact rather than
                 anything about their plan. Found by signing in as a seeded entry_sandbox client
                 against a real local Supabase — the copy read as a tier limit to someone it did not
                 apply to. */
              /* ⚠️ **And the hint has to follow the value.** Left fixed, it told somebody who had
                 just connected HubSpot to go and connect HubSpot — the same class of error as the
                 hardcoded value above, one line further down, and the reason a browser pass caught
                 the value while the tests still passed on the sentence beneath it. */
              hint:
                connections.status === 'success' && connections.data && connections.data.length > 0
                  ? 'Pull records straight onto this device from the Sources screen. The credential is held by the connector service, never by your browser.'
                  : 'Connect Salesforce or HubSpot on the Sources screen and you can pull records straight onto this device. The credential is held by the connector service, never by your browser.',
            },
          ]}
        />
        <HelpText>
          Load another file at any time — Dedupr matches across everything on this device, not just
          within one file.
        </HelpText>
      </Card>
    </>
  );
}

/**
 * The work queue — **the reason this screen exists** (owner decision, 2026-08-15): *"dashboard is
 * workspace … it enables user to quickly work and use next important task, so it's always possible
 * to sort out what's wrong without need to go anywhere else."*
 *
 * So the top item renders its **real control, inline**. Not a link, not a count with a chevron: the
 * same component the dedicated screen uses, mounted here. A dashboard that links out is a summary,
 * and a summary is what this replaces.
 *
 * ⚠️ **One item is expanded, the rest are one click from being.** Mounting six working surfaces at
 * once would make the first paint enormous and every fetch fire whether or not it was wanted; and a
 * queue where everything shouts equally is the scoreboard problem again. Selecting a row swaps the
 * inline control without leaving the screen, which is the ask.
 */
function WorkQueue({
  items,
  orgId,
  localMode,
  onNavigate,
}: {
  items: WorkItem[];
  orgId: string | undefined;
  localMode: boolean;
  onNavigate: (route: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (isAllClear(items)) {
    return (
      <Card title="Nothing is waiting for you" headingLevel={2}>
        <p>
          Every duplicate found has been decided and no change is queued. New records are checked as
          they arrive — this screen fills in when something needs you.
        </p>
      </Card>
    );
  }

  const active = items.find((i) => i.id === openId) ?? items[0];

  return (
    <Card title="Next" headingLevel={2}>
      <h3 className="web-dash__work-title">{active.title}</h3>
      <p className="web-dash__work-why">{active.why}</p>

      {/* ⚠️ **The queue sits ABOVE the control, and that order was decided in a browser.** With it
          below, the connector panel — five providers with their prerequisites — pushed the rest of
          the queue and every figure roughly three screens down, so the one thing this screen is for
          (see what needs you, pick one) required scrolling past the thing you had already picked.
          Switching work is now always one glance away, whatever the active control's height. */}
      {items.length > 1 ? (
        <>
          <h3 className="web-dash__work-title">Also waiting</h3>
          <ul className="web-dash__work-list">
            {items
              .filter((i) => i.id !== active.id)
              .map((item) => (
                <li key={item.id}>
                  {/* Not a link. Selecting swaps the control below, so the work still happens here. */}
                  <Button variant="secondary" onClick={() => setOpenId(item.id)}>
                    {item.title}
                  </Button>
                </li>
              ))}
          </ul>
        </>
      ) : null}

      {/* The control, mounted here rather than linked to. */}
      <div className="web-dash__work-control">
        <WorkControlFor item={active} orgId={orgId} localMode={localMode} onNavigate={onNavigate} />
      </div>
    </Card>
  );
}

/** Maps a queue item to the working component for it. */
function WorkControlFor({
  item,
  orgId,
  localMode,
  onNavigate,
}: {
  item: WorkItem;
  orgId: string | undefined;
  localMode: boolean;
  onNavigate: (route: string) => void;
}) {
  switch (item.control) {
    /* Both land on the same panel deliberately: reconnecting IS connecting again, and `Connect`
       already renders per-provider state (connected / ready / needs setup first) including the
       prerequisites a client must satisfy themselves. Rebuilding a second connector list here would
       be two implementations of this feature's most sensitive surface. */
    case 'connect':
    case 'reconnect':
      return <Connect orgId={orgId} />;
    case 'sync':
      return <SyncPanel />;
    /* ⚠️ **Mounted inline, and the earlier objection is answered by the queue's own shape rather
       than waved away.** The worry was that these are full screens with their own fetches, so
       putting them on the landing route would fire a hosted query on every visit. Only the ACTIVE
       item renders — one control at a time, chosen because it is the most urgent thing outstanding
       — so the query that runs is the one for the work you were going to do anyway. Mounting all
       six at once is the thing that would have been indefensible, and that is not what this does.

       Same components as the dedicated routes, not copies: a second review surface would be two
       implementations of the decisions that reach a customer's system. */
    case 'decide':
      return localMode ? <Matches /> : <HostedMatches orgId={orgId} />;
    case 'approve':
      return localMode ? <Actions onNavigate={onNavigate} /> : <HostedActions orgId={orgId} />;
    case 'findings':
      return localMode ? <Findings onNavigate={onNavigate} /> : <HostedFindings orgId={orgId} />;
    case 'unknown':
      return (
        <HelpText tone="error" role="alert">
          This could not be read just now. Nothing below is reliable until it does — reload to try
          again.
        </HelpText>
      );
  }
}
